import {DateTime} from "luxon";
import {Observable, share, Subject} from "rxjs";
import {container} from "tsyringe";
import {ObjectHelper} from "../../helpers/object.helper";
import {ShortIdHelper} from "../../helpers/short-id.helper";
import {EntityManager} from "../../persistence/entity.manager";
import {Identifiable} from "../identifiable.interface";
import {ChangeSet} from "./change-set.interface";
import {Expose} from "class-transformer";
import {Public} from "../../decorators/public.decorator";
import {typeInfo} from "../../decorators/type-info.decorator";
import {viewComponent} from "../../decorators/view-component.decorator";

type KeyValuePair<T> = { key: keyof T, value: any };

export abstract class Entity<T extends Entity<T> = any> implements Identifiable {

    private _changeSubject = new Subject<Record<PropertyKey, any>>();
    private _lastChange: number = Date.now();
    private _originalValues: Map<keyof T, any> = new Map<keyof T, any>();
    private _persistedSubject = new Subject<void>();
    private _settingSubject = new Subject<Record<PropertyKey, any>>();

    protected _uid: string = ShortIdHelper.generate();

    @typeInfo({isEnumerable: false, isPromise: false, isEntity: false, type: () => String})
    @viewComponent("ha-string-viewer")
    @Public()
    @Expose()
    public get uid(): string {
        return this._uid;
    }

    public set uid(value: string) {
        this._uid = value;
        this._setOriginalValue(`uid`, value);
    }

    private _changedValues: any[] = [];

    public get changedValues(): Array<keyof T> {
        return this._changedValues;
    }

    public get hasChanged(): boolean {
        return this.changedValues.length > 0;
    }

    private _color: string = "#FFFFFF";

    public get color(): string {
        return this._color;
    }

    public set color(value: string) {
        this._color = value;
        this._setOriginalValue(`color`, value);
    }

    /**
     * The text colour on top of `color` (feature plan 0074). `null` means "automatisch": a light or dark text colour
     * is picked against `color` (`resolveRecordTextColor`). A set value is `#rrggbb`.
     */
    private _textColor: string | null = null;

    public get textColor(): string | null {
        return this._textColor;
    }

    public set textColor(value: string | null) {
        this._textColor = value;
        this._setOriginalValue(`textColor`, value);
    }

    private _createdAt: DateTime = DateTime.utc();

    public get createdAt(): DateTime {
        return this._createdAt;
    }

    public set createdAt(value: DateTime) {
        this._createdAt = value;
        this._setOriginalValue(`createdAt`, value);
    }

    private _sorting: number = -1;

    public get sorting(): number {
        return this._sorting;
    }

    public set sorting(value: number) {
        this._sorting = value;
        this._setOriginalValue(`sorting`, value);
    }

    /**
     * @deprecated - not in use
     */
    public get assignedValues(): Array<keyof T> {
        return Array.from(this._originalValues.keys());
    }

    public get changeSet(): ChangeSet<T> {
        const keysOfChangedValues = this.changedValues;
        const changeSet: ChangeSet<T> = {};

        for (const key of keysOfChangedValues) {
            changeSet[key] = {
                last: this._originalValues.get(key)!,
                current: ObjectHelper.getPropertyValue<T>(this as any, key as keyof T)
            }
        }

        return changeSet;
    }

    public get identifier(): string {
        return `${this.uid}-${this.lastChangeTick}`;
    }

    public get lastChangeTick(): number {
        return this._lastChange;
    }

    public get persisted$(): Observable<void> {
        return this._persistedSubject.pipe(
            share()
        );
    }

    /**
     * @deprecated - not in use
     */
    public get valueChanged$(): Observable<Record<string, any>> {
        return this._changeSubject.pipe(
            share()
        );
    }

    /**
     * @deprecated - not in use
     */
    public get valueSet$(): Observable<Record<string, any>> {
        return this._settingSubject.pipe(
            share()
        );
    }

    public async clone(): Promise<T> {
        throw new Error(`clone function not implemented`);
    }

    public reset(): void {
        for (const key of this._changedValues) {
            ObjectHelper.setPropertyValue(this, key, this._originalValues.get(key));
        }
        this._changedValues = [];
        this._resetLastChange();
    }

    public commit(): void {
        for (const key of this._changedValues) {
            this._originalValues.set(key, ObjectHelper.getPropertyValue(this, key));
        }
        this._changedValues = [];
        this._resetLastChange();
    }

    public isDirty(): boolean {
        return this.changedValues.length > 0;
    }

    public isNew(): boolean {
        return container.resolve(EntityManager).isNew(this.uid);
    }

    public isPersisted(): boolean {
        return !container.resolve(EntityManager).isVolatile(this.uid);
    }

    public markAsPersisted(): void {
        for (const key of this._changedValues) {
            this._setOriginalValue(key, (this as Record<string, any>)[key]);
        }
        this.establishColorBaseline();

        this._persistedSubject.next();
    }

    /**
     * Gives `color` and `textColor` a starting value once the entity exists on the server, if they have none yet
     * (feature plan 0074). A record created in this session and left on "automatisch" never ran the setter, so its
     * first later `textColor = …` would only become the starting value — not a change, and the save would send
     * nothing. Called from `markAsPersisted()` and, for a single `create.*`, from `EntityManager`. Writes the map
     * directly: `_setOriginalValue` would also emit on `_settingSubject` on every (also repeated) call.
     *
     * The trap is general — any field whose setter never ran at creation has no starting value. A new nullable
     * field that defaults to null and is set only later belongs in this list too, until the change tracking sets
     * the baseline for every field after a create (ADR-016 follow-up, review 0074-01).
     */
    public establishColorBaseline(): void {
        for (const key of [`color`, `textColor`] as Array<keyof T>) {
            if (!this._originalValues.has(key)) {
                this._originalValues.set(key, (this as Record<string, any>)[key as string]);
            }
        }
    }

    public abstract toString(): string;

    public valueOf(): string {
        return this.uid;
    }

    protected _setOriginalValue(key: keyof T, value: any): void {
        let isSetInitial = false;
        if (!this._originalValues.has(key)) {
            this._originalValues.set(key, value);
            isSetInitial = true;
        }

        const keyValuePairs = this._collectKeyValuePairs();
        this._settingSubject.next(keyValuePairs);

        if (isSetInitial) {
            return;
        }

        if (!this._changedValues.includes(key) && this._originalValues.get(key) !== value) {
            this._changedValues.push(key);
            const keyValuePairs = this._changedValues.map(
                // Optional call, not a bare .valueOf(): clearing a field that previously
                // held a value is an ordinary edit, and null has no valueOf(). Without the
                // guard the TypeError escapes the save flow entirely — no command is built
                // and the save fails with nothing shown to the user.
                key => ({key, value: (this as Record<string, any>)[key]?.valueOf() ?? null})
            );
            this._changeSubject.next(keyValuePairs);
            this._resetLastChange();
        }
    }

    private _collectKeyValuePairs(): Array<KeyValuePair<T>> {
        return Array.from(this._originalValues.keys()).map(key => {
            if (key === `uid`) {
                return {key, value: null};
            }

            let value = ObjectHelper.getPropertyValue<T>(this as any, key) ?? null;
            if (value !== null) {
                value = (Array.isArray(value)
                    ? value.map(val => val.valueOf())
                    : value.valueOf()) as any;
            }

            return {key, value};
        }).filter(({value}) => !!value);
    }

    private _resetLastChange(): void {
        this._lastChange = Date.now();
    }
}